


































  import Vue from 'vue';
  import { Component, Prop, Inject, Watch } from 'vue-property-decorator';
  import { SearchRequest, Result, Filter } from '@empathyco/x-types';
  import { deepEqual, Dictionary } from '@empathyco/x-utils';
  import { State } from '../../../components/decorators/store.decorators';
  import { LIST_ITEMS_KEY } from '../../../components/decorators/injection.consts';
  import { XProvide } from '../../../components/decorators/injection.decorators';
  import { xComponentMixin } from '../../../components/x-component.mixin';
  import { NoElement } from '../../../components/no-element';
  import { RequestStatus } from '../../../store';
  import { QueryFeature, FeatureLocation } from '../../../types/origin';
  import { QueryPreviewInfo, QueryPreviewItem } from '../store/types';
  import { QueriesPreviewConfig } from '../config.types';
  import { queriesPreviewXModule } from '../x-module';
  import { createOrigin } from '../../../utils/origin';
  import { debounce } from '../../../utils/debounce';
  import { DebouncedFunction } from '../../../utils';
  import { createRawFilter } from '../../../__stubs__/index';

  /**
   * Retrieves a preview of the results of a query and exposes them in the default slot,
   * along with the query preview and the totalResults of the search request.
   * By default, it renders the names of the results.
   *
   * @public
   */
  @Component({
    components: {
      NoElement
    },
    mixins: [xComponentMixin(queriesPreviewXModule)]
  })
  export default class QueryPreview extends Vue {
    /**
     * The information about the request of the query preview.
     *
     * @public
     */
    @Prop({ required: true })
    protected queryPreviewInfo!: QueryPreviewInfo;

    /**
     * The origin property for the request.
     *
     * @public
     */
    @Prop()
    protected queryFeature?: QueryFeature;

    /**
     * Number of query preview results to be rendered.
     *
     * @public
     */
    @Prop()
    protected maxItemsToRender?: number;

    /**
     * Debounce time in milliseconds for triggering the search requests.
     * It will default to 0 to fit the most common use case (pre-search),
     * and it would work properly with a 250 value inside empathize.
     */
    @Prop({ default: 0 })
    public debounceTimeMs!: number;

    /**
     * Controls whether the QueryPreview should be removed from the state
     * when the component is destroyed.
     *
     * @public
     */
    @Prop({ default: true })
    public clearOnDestroy!: boolean;

    /**
     * The results preview of the queries preview mounted.
     * It is a dictionary, indexed by the query preview query.
     */
    @State('queriesPreview', 'queriesPreview')
    public previewResults!: Dictionary<QueryPreviewItem>;

    /**
     * As the request is handled in this component, we need
     * the extra params that will be used in the request.
     */
    @State('queriesPreview', 'params')
    public params!: Dictionary<unknown>;

    /**
     * As the request is handled in this component, we need
     * the config that will be used in the request.
     */
    @State('queriesPreview', 'config')
    public config!: QueriesPreviewConfig;

    /**
     * The results to render from the state.
     *
     * @remarks The results list are provided with `items` key. It can be
     * concatenated with list items from components such as `BannersList`, `PromotedsList`,
     * `BaseGrid` or any component that injects the list.
     *
     * @returns A list of results.
     * @public
     */
    @XProvide(LIST_ITEMS_KEY)
    public get results(): Result[] | undefined {
      return this.queryPreviewResults?.results;
    }

    /**
     * It injects the provided {@link FeatureLocation} of the selected query in the search request.
     *
     * @internal
     */
    @Inject({ default: undefined })
    protected location?: FeatureLocation;

    /**
     * The computed request object to be used to retrieve the query preview results.
     *
     * @returns The search request object.
     * @internal
     */
    protected get queryPreviewRequest(): SearchRequest {
      const origin = createOrigin({
        feature: this.queryFeature,
        location: this.location
      });
      const filters = this.queryPreviewInfo.filters?.reduce((filtersList, filterId) => {
        const facetId = filterId.split(':')[0];
        const rawFilter = createRawFilter(filterId);
        filtersList[facetId] = filtersList[facetId]
          ? filtersList[facetId].concat(rawFilter)
          : [rawFilter];

        return filtersList;
      }, {} as Record<string, Filter[]>);

      return {
        query: this.queryPreviewInfo.query,
        rows: this.config.maxItemsToRequest,
        extraParams: { ...this.params, ...this.queryPreviewInfo.extraParams },
        filters: filters,
        ...(origin && { origin })
      };
    }

    /**
     * Gets from the state the results preview of the query preview.
     *
     * @returns The results preview of the actual query preview.
     */
    public get queryPreviewResults(): Partial<QueryPreviewItem> | undefined {
      const previewResults = this.previewResults[this.queryPreviewInfo.query];
      return previewResults?.results
        ? {
            ...previewResults,
            results: previewResults.results.slice(0, this.maxItemsToRender)
          }
        : undefined;
    }

    /**
     * The debounce method to trigger the request after the debounceTimeMs defined.
     *
     * @returns The search request object.
     * @internal
     */
    protected get emitQueryPreviewRequestUpdated(): DebouncedFunction<[SearchRequest]> {
      return debounce(request => {
        this.$x.emit('QueryPreviewRequestUpdated', request, { priority: 0, replaceable: false });
      }, this.debounceTimeMs);
    }

    /**
     * Initialises watcher to emit debounced requests, and first value for the requests.
     *
     * @internal
     */
    protected created(): void {
      this.$watch(
        () => this.queryPreviewRequest,
        (newRequest, oldRequest) => {
          if (!deepEqual(newRequest, oldRequest)) {
            this.emitQueryPreviewRequestUpdated(newRequest);
          }
        }
      );
      this.emitQueryPreviewRequestUpdated(this.queryPreviewRequest);
    }

    /**
     * Cancels the (remaining) requests when the component is destroyed
     * via the `debounce.cancel()` method.
     * If the prop 'clearOnDestroy' is set to true, it also removes the QueryPreview
     * from the state when the component is destroyed.
     *
     * @internal
     */
    protected beforeDestroy(): void {
      this.emitQueryPreviewRequestUpdated.cancel();

      if (this.clearOnDestroy) {
        this.$x.emit('QueryPreviewUnmountedHook', this.queryPreviewInfo.query, {
          priority: 0,
          replaceable: false
        });
      }
    }

    /**
     * Cancels the previous request when the debounced function changes (e.g: the debounceTimeMs
     * prop changes or there is a request in progress that cancels it).
     *
     * @param _new - The new debounced function.
     * @param old - The previous debounced function.
     * @internal
     */
    @Watch('emitQueryPreviewRequestUpdated')
    protected cancelEmitPreviewRequestUpdated(
      _new: DebouncedFunction<[SearchRequest]>,
      old: DebouncedFunction<[SearchRequest]>
    ): void {
      old.cancel();
    }

    /**
     * Emits an event when the query results are loaded or fail to load.
     *
     * @param status - The status of the query preview request.
     * @internal
     */
    @Watch('queryPreviewResults.status')
    emitLoad(status: RequestStatus | undefined): void {
      if (status === 'success') {
        this.$emit(this.results?.length ? 'load' : 'error', this.queryPreviewInfo.query);
      } else if (status === 'error') {
        this.$emit('error', this.queryPreviewInfo.query);
      }
    }
  }
